import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useUnsavedChangesWarning } from '@/hooks/useUnsavedChangesWarning'
import {
  getSellerProfileApi,
  updateSellerProfileApi,
  type SellerProfile,
} from '@/lib/billing'
import {
  LoaderIcon,
  StoreIcon,
  SaveIcon,
  BuildingIcon,
} from 'lucide-react'

export const Route = createFileRoute('/dashboard/admin/seller-profile')({
  component: () => {
    const [form, setForm] = useState<{
      legal_entity_name: string
      registration_number: string
      vat_number: string
      duns_number: string
      registered_address: string
      registration_country: string
      contact_email: string
    }>({
      legal_entity_name: '',
      registration_number: '',
      vat_number: '',
      duns_number: '',
      registered_address: '',
      registration_country: '',
      contact_email: '',
    })
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [dirty, setDirty] = useState(false)
    useUnsavedChangesWarning(dirty)

    useEffect(() => {
      let cancelled = false
      async function load() {
        try {
          const res = await getSellerProfileApi()
          if (!cancelled && res.seller) {
            const s: SellerProfile = res.seller
            setForm({
              legal_entity_name: s.legal_name || '',
              registration_number: s.registration_number || '',
              vat_number: s.vat_number || '',
              duns_number: s.duns_number || '',
              registered_address: s.registered_address || '',
              registration_country: s.registration_country || '',
              contact_email: s.contact_email || '',
            })
          }
        } catch (err) {
          if (!cancelled) {
            toast.error(err instanceof Error ? err.message : 'Failed to load seller profile')
          }
        } finally {
          if (!cancelled) setLoading(false)
        }
      }
      load()
      return () => { cancelled = true }
    }, [])

    const handleChange = (key: keyof typeof form, value: string) => {
      setForm((prev) => ({ ...prev, [key]: value }))
      setDirty(true)
    }

    const handleSave = async () => {
      if (!form.legal_entity_name.trim()) {
        toast.error('Legal entity name is required')
        return
      }
      if (form.registration_country && !/^[A-Za-z]{2}$/.test(form.registration_country)) {
        toast.error('Registration country must be a 2-letter code (e.g. EE, IE)')
        return
      }
      setSaving(true)
      try {
        const res = await updateSellerProfileApi({
          legal_entity_name: form.legal_entity_name.trim(),
          registration_number: form.registration_number || null,
          vat_number: form.vat_number || null,
          duns_number: form.duns_number || null,
          registered_address: form.registered_address || null,
          registration_country: form.registration_country.toUpperCase() || null,
          contact_email: form.contact_email || null,
        })
        setDirty(false)
        toast.success(res.message)
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to save seller profile')
      } finally {
        setSaving(false)
      }
    }

    if (loading) {
      return (
        <div className="mx-auto max-w-2xl">
          <div className="flex items-center justify-center rounded-xl border border-border bg-card py-16">
            <LoaderIcon className="size-5 animate-spin text-muted-foreground" />
            <p className="ml-2 text-sm text-muted-foreground">Loading seller profile…</p>
          </div>
        </div>
      )
    }

    return (
      <div className="mx-auto max-w-2xl">
        <div className="mb-6">
          <div className="flex items-center gap-3">
            <div className="flex size-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <StoreIcon className="size-5" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-foreground">Seller profile</h1>
              <p className="text-sm text-muted-foreground">
                PeerPass legal identity shown as the issuer on every invoice and PDF.
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-6">
          <div className="mb-5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground/60">
            <BuildingIcon className="size-4 text-primary" />
            Invoice seller (PeerPass)
          </div>

          <div className="space-y-4">
            <Field label="Legal entity name *" hint="e.g. PeerPass OÜ">
              <Input
                value={form.legal_entity_name}
                onChange={(e) => handleChange('legal_entity_name', e.target.value)}
                placeholder="PeerPass OÜ"
              />
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Registration number" hint="Government registration number">
                <Input
                  value={form.registration_number}
                  onChange={(e) => handleChange('registration_number', e.target.value)}
                  placeholder="00000000"
                />
              </Field>
              <Field label="VAT number" hint="EU VAT identification number">
                <Input
                  value={form.vat_number}
                  onChange={(e) => handleChange('vat_number', e.target.value)}
                  placeholder="EE000000000"
                />
              </Field>
            </div>

            <Field label="D-U-N-S number" hint="Dun & Bradstreet D-U-N-S number (9 digits)">
              <Input
                value={form.duns_number}
                onChange={(e) => handleChange('duns_number', e.target.value)}
                placeholder="000000000"
                maxLength={9}
              />
            </Field>

            <Field label="Registered address">
              <Input
                value={form.registered_address}
                onChange={(e) => handleChange('registered_address', e.target.value)}
                placeholder="Tallinn, Estonia"
              />
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Registration country" hint="2-letter ISO code (e.g. EE)">
                <Input
                  value={form.registration_country}
                  onChange={(e) => handleChange('registration_country', e.target.value.toUpperCase())}
                  maxLength={2}
                  placeholder="EE"
                />
              </Field>
              <Field label="Contact email" hint="Billing/accounts email shown on invoices">
                <Input
                  type="email"
                  value={form.contact_email}
                  onChange={(e) => handleChange('contact_email', e.target.value)}
                  placeholder="billing@gopeerpass.com"
                />
              </Field>
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <Button variant="outline" onClick={() => setDirty(false)} disabled={!dirty || saving}>
              Discard
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <LoaderIcon className="mr-2 size-4 animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <SaveIcon className="mr-2 size-4" />
                  Save seller profile
                </>
              )}
            </Button>
          </div>
        </div>

        <p className="mt-4 text-xs text-muted-foreground">
          The seller block is rendered on every invoice (web view, PDF download, and payment
          receipt emails). Historical invoices keep the buyer identity snapshot taken at
          generation time.
        </p>
      </div>
    )
  },
})

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <Label className="mb-1.5 block text-xs font-medium text-foreground">{label}</Label>
      {children}
      {hint && <p className="mt-1 text-[11px] text-muted-foreground/70">{hint}</p>}
    </div>
  )
}
